import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { KeyRound, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      toast.error(t('errors.fillAllFields'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('errors.passwordMinLength'));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t('errors.passwordsMismatch'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      toast.success(t('auth.passwordChanged'));
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.response?.data?.detail || t('errors.resetPasswordError'));
      toast.error(error.response?.data?.detail || t('errors.resetPasswordError'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <CheckCircle size={48} style={{ color: '#22c55e' }} />
            <h1>{t('auth.passwordChanged')}</h1>
            <p style={{ marginTop: '1rem' }}>
              {t('auth.passwordChangedDesc')}
            </p>
          </div>

          <Button
            onClick={() => navigate('/login')}
            className="auth-submit-btn"
            style={{ marginTop: '1.5rem' }}
          >
            {t('auth.loginToSystem')}
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <XCircle size={48} style={{ color: '#ef4444' }} />
            <h1>{t('common.error')}</h1>
            <p style={{ marginTop: '1rem', color: '#ef4444' }}>
              {error}
            </p>
            <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
              {t('auth.linkExpiredOrUsed')}
            </p>
          </div>

          <div className="auth-footer" style={{ marginTop: '1.5rem' }}>
            <Link to="/forgot-password">
              {t('auth.requestNewLink')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <KeyRound size={32} />
          <h1>{t('auth.newPassword')}</h1>
          <p>{t('auth.enterNewPassword')}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">{t('auth.newPassword')}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.minSixChars')}
              disabled={loading}
              minLength={6}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('auth.repeatPassword')}
              disabled={loading}
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="auth-submit-btn"
          >
            {loading ? t('common.saving') : t('auth.savePassword')}
          </Button>
        </form>

        <div className="auth-footer">
          <p>
            <Link to="/login">
              {t('auth.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
